import { buildUrl } from 'local/url'
import { User, UserManager } from 'oidc-client-ts'

import { TenantConfiguration } from '../configure'

import createSessionMonitor from './createSessionMonitor'
import { Manager, EventHandler, SessionTimeoutReason, ReauthenticationResult } from './types'
import { emptyMethod, createEventHandlers } from './utils'

// https://github.com/authts/oidc-client-ts

const sessionEvents: SessionTimeoutReason[] = ['sessiontimeout', 'closebrowser', 'redirecttologin']

// Only creating the event handlers once, to prevent mismatch between creating handlers
// and adding listeners.
const eventHandlers = {
	sessiontimeout: createEventHandlers<SessionTimeoutReason>(),
	userreauthenticated: createEventHandlers<ReauthenticationResult>(),
}

export default (config: TenantConfiguration, anonymous: boolean) => {
	const baseUrl = buildUrl(process.env.APP_ROOT_URL)

	const userManager = new UserManager({
		authority: config.uls.url,
		client_id: config.uls.clientId || 'DamUI',
		popup_redirect_uri: `${baseUrl}auth/signinpopupcallback`,
		redirect_uri: `${baseUrl}auth/signinredirectcallback`,
		silent_redirect_uri: `${baseUrl}auth/signinsilentcallback`,
		//TODO If we have a way to logout SSO we can uncomment this out.
		//Removing this on usermanager will allow uls to redirect to its logout page instead of re-login again.
		post_logout_redirect_uri: `${baseUrl}auth/signoutredirectcallback`,
		popup_post_logout_redirect_uri: `${baseUrl}auth/popupsignoutcallback`,
		response_type: 'code',
		scope: 'api ui openid api-internal',
		filterProtocolClaims: true,
		loadUserInfo: true,
		automaticSilentRenew: true,
		monitorSession: false,
	})

	let preventDoubleReauthentication = false

	const handleUserReauthenticated = (result: ReauthenticationResult) => {
		userManager.startSilentRenew()
		eventHandlers.userreauthenticated.emit(result)
	}

	let lastFetchedSub: string
	const getUser = async () => {
		return userManager
			.getUser()
			.catch(emptyMethod)
			.then((user) => {
				if (!user || user.expired) {
					return userManager.signinSilent().catch(emptyMethod)
				}
				return user
			})
			.then((user: void | User) => {
				if (!user || user.expired) {
					return undefined
				}
				lastFetchedSub = user.profile.sub
				return user
			})
	}

	const popUpWindowFeatures = {
		location: 'no',
		toolbar: 'no',
		width: 800,
		height: 800,
		left: 100,
		top: 100,
	}

	const manager: Manager = {
		getAuthHeader: () => {
			if (anonymous) {
				return null
			}
			return getUser().then((user) => {
				if (!user) {
					return undefined
				}
				return `Bearer ${user.access_token}`
			})
		},
		signin: (returnTo) => {
			// TODO: error handling
			return userManager.clearStaleState().then(() => {
				return userManager.signinRedirect({
					redirectMethod: 'replace',
					state: { returnTo },
				})
			})
		},
		signinCallback: (type) => {
			// TODO: error handling
			switch (type) {
				case 'redirect':
					return userManager.signinRedirectCallback()
				case 'popup':
					return userManager.signinPopupCallback()
				case 'silent':
					return userManager.signinSilentCallback()
			}
		},
		signout: () => {
			// TODO: error handling
			if (!anonymous) {
				return getUser().then((user) => {
					sessionMonitor.expireUserSession(user.profile?.sid, user.access_token)
					return userManager.signoutRedirect({
						state: {
							returnTo: window.location,
						},
					})
				})
			}
		},
		signoutCallback: (type) => {
			// TODO: error handling
			switch (type) {
				case 'redirect':
					return userManager
						.signoutRedirectCallback()
						.then((signoutCallbackResponse) => {
							return (signoutCallbackResponse?.userState as any)?.returnTo
						})
						.catch(emptyMethod)
				case 'popup':
					return userManager.signoutPopupCallback().catch(emptyMethod).then(emptyMethod)
			}
		},
		signinPopup: () => {
			return userManager
				.signinPopup({
					prompt: 'login',
					popupWindowFeatures: popUpWindowFeatures,
				})
				.catch(emptyMethod)
				.then((user) => {
					// TODO: check if the user is the same user as current user.
					if (user && !user.expired) {
						// Re-activate the user
						// TODO: is this the correct check?

						handleUserReauthenticated(user.profile.sub === lastFetchedSub ? 'sameuser' : 'differentuser')

						// TODO: Since the sessionMonitor.postMessage was deleted, will create same as "Inform all other instances that the user reauthenticated".

						// In case the session monitor raises the 'userreauthenticated' back to the originating window. prevent that.
						// This prevention is needed because the event handler does a silent signin, but, the user just got signed in here
						// Therefore, that is a redundant and unwanted side effect.
						preventDoubleReauthentication = true

						// Ensure the flag is cleared again, for when another tab calls the event.
						setTimeout(() => (preventDoubleReauthentication = false), 1000)
					}
				})
		},
		keepAlive: () => {
			if (!anonymous) {
				getUser()
					.then((user) => {
						sessionMonitor.keepUserSessionActive(user.profile?.sid, user.access_token)
					})
					.catch(() => {
						//In case there are no user profile available then we will render the timeout page
						sessionMonitor.renderTimeOutPage('sessiontimeout')
					})
			}
		},
		addEventListener: (type: 'sessiontimeout' | 'userreauthenticated', handler: EventHandler<any>) => {
			eventHandlers[type].addListener(handler)
			return () => eventHandlers[type].removeListener(handler)
		},
	}

	userManager.events.addUserLoaded(() => {
		getUser().then((user) => {
			sessionMonitor.updateExpiration(
				user.expires_in - userManager.settings.accessTokenExpiringNotificationTimeInSeconds,
				user.access_token,
				user.profile?.sid,
			)
		})
	})

	const sessionMonitor = createSessionMonitor(config.uls.url)

	sessionEvents.forEach((sessionEventName) => {
		sessionMonitor.addEventListener(sessionEventName, () => {
			if (!anonymous) {
				getUser().then((user) => {
					userManager.stopSilentRenew()
					userManager
						.removeUser()
						.then(() => {
							userManager.clearStaleState()
							eventHandlers.sessiontimeout.emit(sessionEventName)

							const idTokenHint = user?.id_token
							const sid = user?.profile?.sid
							const authHeader = user?.access_token

							// Expire and request to signout the user without redirect.
							if (sid && authHeader) {
								sessionMonitor.expireUserSession(sid, authHeader)
							}

							if (idTokenHint) {
								sessionMonitor.createSignoutRequestWithoutRedirect(idTokenHint)
							}
						})
						.catch(emptyMethod)
				})
			}
		})
	})

	sessionMonitor.addEventListener('userreauthenticated', () => {
		if (preventDoubleReauthentication) {
			preventDoubleReauthentication = false
			return
		}

		userManager
			.signinSilent()
			.catch(emptyMethod)
			.then((user) => {
				if (user && !user.expired) {
					handleUserReauthenticated(user.profile.sub === lastFetchedSub ? 'sameuser' : 'differentuser')
				}
			})
	})

	return manager
}
